<template>
  <div>
    <Label class="text-xs text-muted-foreground">Animation</Label>
    <Select :model-value="animation?.type || 'none'" @update:model-value="handleAnimationChange">
      <SelectTrigger class="mt-2 h-8">
        <SelectValue placeholder="None" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None</SelectItem>
        <SelectItem value="shake">Shake</SelectItem>
        <SelectItem value="scroll">Scroll</SelectItem>
        <SelectItem value="pulse">Pulse</SelectItem>
        <SelectItem value="bounce">Bounce</SelectItem>
        <SelectItem value="rotate">Rotate</SelectItem>
      </SelectContent>
    </Select>

    <!-- Animation Parameters -->
    <div v-if="animation?.type" class="grid grid-cols-2 gap-2 mt-3">
      <!-- Shake parameters -->
      <template v-if="animation.type === 'shake'">
        <div>
          <Label class="text-xs text-muted-foreground">Speed (s)</Label>
          <NumberField :model-value="animation.speed" @update:model-value="updateSpeed" :step="0.1" class="mt-1">
            <NumberFieldContent>
              <NumberFieldInput class="h-8 text-sm" />
            </NumberFieldContent>
          </NumberField>
        </div>
        <div>
          <Label class="text-xs text-muted-foreground">Amplitude</Label>
          <NumberField :model-value="animation.amplitude" @update:model-value="updateAmplitude" :step="1" class="mt-1">
            <NumberFieldContent>
              <NumberFieldInput class="h-8 text-sm" />
            </NumberFieldContent>
          </NumberField>
        </div>
      </template>

      <!-- Scroll parameters -->
      <div v-if="animation.type === 'scroll'" class="col-span-2">
        <Label class="text-xs text-muted-foreground">Speed (s)</Label>
        <NumberField :model-value="animation.speed" @update:model-value="updateSpeed" :step="0.5" class="mt-1">
          <NumberFieldContent>
            <NumberFieldInput class="h-8 text-sm" />
          </NumberFieldContent>
        </NumberField>
      </div>

      <!-- Pulse parameters -->
      <template v-if="animation.type === 'pulse'">
        <div>
          <Label class="text-xs text-muted-foreground">Speed (s)</Label>
          <NumberField :model-value="animation.speed" @update:model-value="updateSpeed" :step="0.1" class="mt-1">
            <NumberFieldContent>
              <NumberFieldInput class="h-8 text-sm" />
            </NumberFieldContent>
          </NumberField>
        </div>
        <div>
          <Label class="text-xs text-muted-foreground">Amplitude</Label>
          <NumberField :model-value="animation.amplitude" @update:model-value="updateAmplitude" :step="0.1" class="mt-1">
            <NumberFieldContent>
              <NumberFieldInput class="h-8 text-sm" />
            </NumberFieldContent>
          </NumberField>
        </div>
      </template>

      <!-- Bounce parameters -->
      <template v-if="animation.type === 'bounce'">
        <div>
          <Label class="text-xs text-muted-foreground">Speed (s)</Label>
          <NumberField :model-value="animation.speed" @update:model-value="updateSpeed" :step="0.1" class="mt-1">
            <NumberFieldContent>
              <NumberFieldInput class="h-8 text-sm" />
            </NumberFieldContent>
          </NumberField>
        </div>
        <div>
          <Label class="text-xs text-muted-foreground">Amplitude</Label>
          <NumberField :model-value="animation.amplitude" @update:model-value="updateAmplitude" :step="1" class="mt-1">
            <NumberFieldContent>
              <NumberFieldInput class="h-8 text-sm" />
            </NumberFieldContent>
          </NumberField>
        </div>
      </template>

      <!-- Rotate parameters -->
      <template v-if="animation.type === 'rotate'">
        <div>
          <Label class="text-xs text-muted-foreground">Speed</Label>
          <NumberField :model-value="animation.speed" @update:model-value="updateSpeed" :step="1" class="mt-1">
            <NumberFieldContent>
              <NumberFieldInput class="h-8 text-sm" />
            </NumberFieldContent>
          </NumberField>
        </div>
        <div class="flex items-end">
          <div class="flex items-center space-x-2 h-8">
            <Switch
              :model-value="(animation.amplitude ?? 1) >= 0"
              @update:model-value="updateDirection"
            />
            <Label class="text-xs">Clockwise</Label>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  NumberField,
  NumberFieldContent,
  NumberFieldInput,
} from '@/components/ui/number-field'

interface LayerAnimation {
  type: 'shake' | 'scroll' | 'pulse' | 'bounce' | 'rotate'
  enabled: boolean
  speed?: number
  amplitude?: number
}

interface Props {
  animation?: LayerAnimation
}

const props = defineProps<Props>()

const emit = defineEmits<{
  update: [animation: LayerAnimation | undefined]
}>()

const handleAnimationChange = (value: unknown) => {
  const stringValue = String(value)
  if (!stringValue || stringValue === 'none') {
    emit('update', undefined)
    return
  }

  const baseAnimation = {
    type: value as LayerAnimation['type'],
    enabled: true
  }

  // Set default values based on animation type
  switch (value) {
    case 'shake':
      emit('update', { ...baseAnimation, type: 'shake', speed: 1, amplitude: 5 })
      break
    case 'scroll':
      emit('update', { ...baseAnimation, type: 'scroll', speed: 10 })
      break
    case 'pulse':
      emit('update', { ...baseAnimation, type: 'pulse', speed: 2, amplitude: 1 })
      break
    case 'bounce':
      emit('update', { ...baseAnimation, type: 'bounce', speed: 1, amplitude: 5 })
      break
    case 'rotate':
      emit('update', { ...baseAnimation, type: 'rotate', speed: 10, amplitude: 1 })
      break
  }
}

const updateSpeed = (value: number) => {
  if (props.animation) {
    emit('update', { ...props.animation, speed: value })
  }
}

const updateAmplitude = (value: number) => {
  if (props.animation) {
    emit('update', { ...props.animation, amplitude: value })
  }
}

const updateDirection = (clockwise: boolean) => {
  if (props.animation) {
    emit('update', { ...props.animation, amplitude: clockwise ? 1 : -1 })
  }
}
</script>
